import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { ClipboardList, Users, UserCog, Package, CheckCircle2, Award, Truck, Factory, Clock, ArrowLeft, AlertTriangle, Calendar, TrendingUp, ChevronRight } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, PieChart, Pie, Cell, Legend } from "recharts";
import { aggregateByCanonical, canonicalColor, productBreakdownByCanonical } from "../lib/brandColors";
import { formatDate } from "../lib/format";
import UserBadge from "../components/UserBadge";

const COLORS = ["#224A38", "#D35400", "#3E7B5B", "#E89B2D", "#3B75B2", "#B23B3B"]; // used for non-brand charts

function StatCard({ icon: Icon, label, value, tint = "forest", testid, onClick, hint }) {
  const tints = {
    forest: "bg-forest-soft text-forest",
    accent: "bg-terracotta/10 text-terracotta",
    warn: "bg-yellow-50 text-yellow-700",
    info: "bg-blue-50 text-blue-700",
    prod: "bg-purple-50 text-purple-700",
    ship: "bg-orange-50 text-orange-700",
  };
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-testid={testid}
      className={`card-surface p-6 text-left w-full animate-fade-up ${onClick ? "cursor-pointer hover:-translate-y-0.5 hover:shadow-md transition-all duration-200" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-figtree text-sm text-ink-soft">{label}</div>
          <div className="font-outfit font-bold text-2xl lg:text-3xl text-ink mt-2 truncate">{value}</div>
          {hint && <div className="font-figtree text-xs text-ink-mute mt-1">{hint}</div>}
        </div>
        <div className={`h-11 w-11 rounded-xl grid place-items-center flex-shrink-0 ${tints[tint]}`}>
          <Icon className="h-5 w-5" strokeWidth={1.75} />
        </div>
      </div>
    </Cmp>
  );
}

function MiniStat({ label, value, tint = "forest", hint, testid }) {
  const tints = {
    forest: "text-forest",
    accent: "text-terracotta",
    info: "text-blue-700",
    warn: "text-yellow-700",
  };
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="font-figtree text-xs text-ink-soft uppercase tracking-wide">{label}</div>
      <div className={`font-outfit font-bold text-2xl mt-1 ${tints[tint] || tints.forest}`}>{value}</div>
      {hint && <div className="font-figtree text-xs text-ink-mute mt-0.5">{hint}</div>}
    </div>
  );
}

function FunnelBar({ label, value, total, color, last }) {
  const pct = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className={last ? "" : "mb-4"}>
      <div className="flex items-center justify-between font-figtree mb-1.5">
        <span className="flex items-center gap-2 text-sm font-medium text-ink">
          <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
          {label}
        </span>
        <span className="text-sm text-ink-soft">
          <span className="font-outfit font-semibold text-ink">{value}</span>
          <span className="ml-2 text-xs">· {pct.toFixed(0)}%</span>
        </span>
      </div>
      <div className="h-3 rounded-full bg-bone overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${Math.max(pct, value > 0 ? 3 : 0)}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [drilldownBrand, setDrilldownBrand] = useState(null); // "Dew Drop" | "Hug Me" | ...
  useEffect(() => { api.get("/dashboard/summary").then(r => setData(r.data)).finally(() => setLoading(false)); }, []);

  // Canonical brand aggregation for the "Orders by Brand" pie.
  const brandPie = useMemo(() => aggregateByCanonical(data?.orders_by_brand || []), [data]);

  // Per-brand product breakdown for the drill-down pie.
  const productBreakdown = useMemo(
    () => productBreakdownByCanonical(data?.orders_by_brand || []),
    [data]
  );

  const drilldownData = drilldownBrand
    ? Object.entries(productBreakdown[drilldownBrand] || {})
        .filter(([, q]) => q > 0)
        .map(([product, quantity]) => ({ product, quantity }))
        .sort((a, b) => b.quantity - a.quantity)
    : [];

  // Drill-down slice palette — achromatic (black → grey → white) so each product
  // is easy to distinguish. Same 4-shade ramp is used across all three brands;
  // only the brand-coloured title/badge above the pie carries the brand identity.
  const PRODUCT_PALETTE = ["#111827", "#4B5563", "#9CA3AF", "#E5E7EB"];
  const drilldownColors = PRODUCT_PALETTE;

  if (loading || !data) {
    return (
      <div>
        <PageHeader title="Dashboard" subtitle="Overview" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="card-surface p-6 h-28 animate-pulse" />)}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page">
      <PageHeader title="Dashboard" subtitle="A calm overview of your orders & dispatch" />

      {/* Top KPI row — all clickable */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard icon={ClipboardList} label="Total Orders" value={data.total_orders} tint="forest" testid="kpi-total-orders" onClick={() => nav("/orders")} hint="View all" />
        <StatCard icon={CheckCircle2} label="Completed Orders" value={data.completed_orders} tint="info" testid="kpi-completed-orders" onClick={() => nav("/orders?status=completed")} hint="Delivered to buyer" />
        <StatCard icon={Clock} label="Pending Orders" value={data.pending_orders} tint="warn" testid="kpi-pending-orders" onClick={() => nav("/orders?status=pending")} hint="Not started yet" />
        <StatCard
          icon={Package}
          label="Cartons Sold"
          value={(data.cartons_sold || 0).toLocaleString("en-IN")}
          tint="accent"
          testid="kpi-cartons-sold"
          onClick={() => nav("/orders")}
          hint={data.pacs_sold ? `+ ${data.pacs_sold} pacs` : "Total across all orders"}
        />
      </div>

      {/* Second KPI row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-6">
        <StatCard icon={Factory} label="In Production" value={data.in_production_orders} tint="prod" testid="kpi-in-production" onClick={() => nav("/orders?status=in_production")} />
        <StatCard icon={Truck} label="Dispatched" value={data.dispatched_orders} tint="ship" testid="kpi-dispatched" onClick={() => nav("/orders?status=dispatched")} hint="On the way" />
        <StatCard icon={Users} label="Buyers" value={data.party_count} tint="accent" testid="kpi-buyers" onClick={() => nav("/parties")} />
        <StatCard icon={UserCog} label="Employees" value={data.employee_count} tint="info" testid="kpi-employees" onClick={() => nav("/employees")} />
      </div>

      {/* This Week strip */}
      <div className="mt-8">
        <h3 className="font-outfit font-semibold text-lg text-ink mb-3 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-forest" /> This Week at a glance
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <MiniStat label="Orders Today"      value={data.orders_today}         tint="forest" testid="mini-orders-today" />
          <MiniStat label="Orders This Week"  value={data.orders_this_week}     tint="info"   testid="mini-orders-week" />
          <MiniStat label="Cartons This Week" value={(data.cartons_this_week || 0).toLocaleString("en-IN")} tint="accent" testid="mini-cartons-week" hint={data.pacs_this_week ? `+ ${data.pacs_this_week} pacs` : ""} />
          <MiniStat label="Completed This Week" value={data.completed_this_week} tint="info"   testid="mini-completed-week" hint={`${data.dispatched_this_week} dispatched`} />
        </div>
      </div>

      {/* Overdue alert */}
      {data.overdue_count > 0 && (
        <button
          onClick={() => nav("/orders?status=pending")}
          data-testid="overdue-alert"
          className="w-full mt-6 card-surface p-4 border-l-4 border-red-500 flex items-center justify-between hover:bg-red-50/40 transition-colors duration-200 text-left"
        >
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-red-100 text-red-700 grid place-items-center">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <div className="font-outfit font-bold text-red-800">{data.overdue_count} overdue order{data.overdue_count !== 1 ? "s" : ""}</div>
              <div className="font-figtree text-sm text-red-700/80">Dispatch deadline has passed — needs immediate attention.</div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-red-700" />
        </button>
      )}

      {/* Status funnel */}
      <div className="mt-8">
        <h3 className="font-outfit font-semibold text-lg text-ink mb-1 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-forest" /> Order Pipeline
        </h3>
        <p className="font-figtree text-sm text-ink-soft mb-4">Live snapshot of where every order stands</p>
        <div className="card-surface p-6">
          <FunnelBar label="Pending"       value={data.pending_orders}       total={data.total_orders} color="#EAB308" />
          <FunnelBar label="In Production" value={data.in_production_orders} total={data.total_orders} color="#8B5CF6" />
          <FunnelBar label="Dispatched"    value={data.dispatched_orders}    total={data.total_orders} color="#F97316" />
          <FunnelBar label="Completed"     value={data.completed_orders}     total={data.total_orders} color="#10B981" last />
        </div>
      </div>

      {/* Third row — highlights */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        <StatCard icon={Award} label="Top Buyer" value={data.top_buyers[0]?.name || "—"} tint="forest" testid="kpi-top-buyer" onClick={() => nav("/parties")} hint={data.top_buyers[0] ? `${data.top_buyers[0].count} orders · ${data.top_buyers[0].cartons || 0} cartons` : ""} />
        <StatCard icon={Award} label="Top Employee" value={data.top_employees[0]?.name || "—"} tint="accent" testid="kpi-top-employee" onClick={() => nav("/employees")} hint={data.top_employees[0] ? `${data.top_employees[0].count} orders · ${data.top_employees[0].cartons || 0} cartons` : ""} />
        <StatCard icon={Package} label="Top Brand" value={data.orders_by_brand[0]?.brand || "—"} tint="forest" testid="kpi-top-brand" onClick={() => nav("/products")} hint={data.orders_by_brand[0] ? `${data.orders_by_brand[0].quantity} units` : ""} />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-8">
        <div className="card-surface p-6 lg:col-span-2">
          <h3 className="font-outfit font-semibold text-lg text-ink">Orders Trend</h3>
          <p className="font-figtree text-sm text-ink-soft mb-4">Units ordered over the last 6 months</p>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <LineChart data={data.trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E0" />
                <XAxis dataKey="month" stroke="#5C6B61" fontSize={12} />
                <YAxis stroke="#5C6B61" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E8E6E0", fontFamily: "Figtree" }} />
                <Line type="monotone" dataKey="quantity" stroke="#224A38" strokeWidth={2.5} dot={{ r: 4 }} name="Units" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-6">
          <h3 className="font-outfit font-semibold text-lg text-ink mb-1">Orders by Brand</h3>
          <p className="font-figtree text-sm text-ink-soft mb-4">
            {brandPie.length === 0 ? "Top brands by units" : "Click a slice to see product breakdown"}
          </p>
          {brandPie.length === 0 ? (
            <div className="text-ink-soft text-sm py-16 text-center">No orders yet</div>
          ) : (
            <div style={{ width: "100%", height: 260 }} data-testid="brand-pie-chart">
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={brandPie}
                    dataKey="quantity"
                    nameKey="brand"
                    innerRadius={55}
                    outerRadius={90}
                    paddingAngle={3}
                    onClick={(entry) => {
                      if (entry && entry.brand) setDrilldownBrand(entry.brand);
                    }}
                    cursor="pointer"
                  >
                    {brandPie.map((entry, i) => (
                      <Cell
                        key={i}
                        fill={entry.color}
                        data-testid={`brand-pie-slice-${entry.brand.replace(/\s/g, "-")}`}
                        style={{ cursor: "pointer", outline: "none" }}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: "1px solid #E8E6E0", fontFamily: "Figtree" }}
                    formatter={(v, _n, p) => [`${v} units`, p?.payload?.brand]}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Figtree" }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
          {/* Clickable brand chips (mobile-friendly alternative to clicking the slice) */}
          {brandPie.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {brandPie.map((b) => (
                <button
                  key={b.brand}
                  onClick={() => setDrilldownBrand(b.brand)}
                  data-testid={`brand-chip-${b.brand.replace(/\s/g, "-")}`}
                  className="px-3 py-1.5 rounded-full text-xs font-figtree font-semibold flex items-center gap-1.5 border border-line hover:bg-sand transition-colors duration-200"
                >
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: b.color }} />
                  {b.brand} <span className="text-ink-soft font-normal">· {b.quantity}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {data.orders_by_size.length > 0 && (
        <div className="card-surface p-6 mt-8">
          <h3 className="font-outfit font-semibold text-lg text-ink mb-1">Orders by Size</h3>
          <p className="font-figtree text-sm text-ink-soft mb-4">Units ordered per size</p>
          <div style={{ width: "100%", height: 300 }}>
            <ResponsiveContainer>
              <BarChart data={data.orders_by_size}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E0" />
                <XAxis dataKey="size" stroke="#5C6B61" fontSize={13} />
                <YAxis stroke="#5C6B61" fontSize={12} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E8E6E0", fontFamily: "Figtree" }} />
                <Bar dataKey="quantity" fill="#224A38" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Rankings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        <div className="card-surface p-6">
          <h3 className="font-outfit font-semibold text-lg text-ink mb-4">Top Buyers</h3>
          {data.top_buyers.length === 0 ? (
            <div className="text-ink-soft text-sm py-8 text-center">No orders recorded yet</div>
          ) : (
            <ul className="space-y-3">
              {data.top_buyers.map((b, i) => (
                <li key={b.name} className="flex items-center justify-between p-3 rounded-lg bg-bone">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-forest text-white grid place-items-center font-outfit font-semibold flex-shrink-0">{i + 1}</div>
                    <span className="font-figtree font-medium text-ink truncate">{b.name}</span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <div className="font-outfit font-semibold text-forest">{b.count} orders</div>
                    <div className="font-figtree text-xs text-ink-soft">{b.cartons || 0} cartons</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="card-surface p-6">
          <h3 className="font-outfit font-semibold text-lg text-ink mb-4">Top Employees</h3>
          {data.top_employees.length === 0 ? (
            <div className="text-ink-soft text-sm py-8 text-center">Assign employees to orders to see rankings</div>
          ) : (
            <ul className="space-y-3">
              {data.top_employees.map((b, i) => (
                <li key={b.name} className="flex items-center justify-between p-3 rounded-lg bg-bone">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-9 w-9 rounded-lg bg-terracotta text-white grid place-items-center font-outfit font-semibold flex-shrink-0">{i + 1}</div>
                    <span className="font-figtree font-medium text-ink truncate">{b.name}</span>
                  </div>
                  <div className="text-right flex-shrink-0 ml-3">
                    <div className="font-outfit font-semibold text-terracotta">{b.count} orders</div>
                    <div className="font-figtree text-xs text-ink-soft">{b.cartons || 0} cartons</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      {/* Recent Orders */}
      {data.recent_orders && data.recent_orders.length > 0 && (
        <div className="mt-8">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-outfit font-semibold text-lg text-ink flex items-center gap-2">
              <Clock className="h-5 w-5 text-forest" /> Recent Orders
            </h3>
            <button
              onClick={() => nav("/orders")}
              data-testid="view-all-orders-btn"
              className="text-sm font-figtree font-semibold text-forest hover:text-forest-hover flex items-center gap-1"
            >
              View all <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <div className="card-surface overflow-hidden">
            <ul className="divide-y divide-line">
              {data.recent_orders.map((o) => {
                const statusColor = {
                  pending: "bg-yellow-50 text-yellow-700",
                  in_production: "bg-purple-50 text-purple-700",
                  dispatched: "bg-orange-50 text-orange-700",
                  completed: "bg-emerald-50 text-emerald-700",
                  cancelled: "bg-red-50 text-red-700",
                }[o.status] || "bg-gray-50 text-gray-700";
                return (
                  <li
                    key={o.id}
                    onClick={() => nav(`/orders/${o.id}`)}
                    data-testid={`recent-order-${o.id}`}
                    className="p-4 hover:bg-bone/60 cursor-pointer flex items-center gap-4"
                  >
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-figtree font-semibold flex-shrink-0 ${statusColor}`}>
                      {(o.status || "").replace("_", " ")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="font-figtree font-medium text-ink truncate">{o.party_name}</div>
                      <div className="font-figtree text-xs text-ink-soft">
                        {o.items_count} item{o.items_count !== 1 ? "s" : ""} · {o.total_qty} units
                        {o.brands?.length > 0 && (
                          <span className="text-ink-mute"> — {o.brands.slice(0, 2).join(", ")}{o.brands.length > 2 && ` +${o.brands.length - 2}`}</span>
                        )}
                      </div>
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-1 flex-shrink-0">
                      <div className="text-xs font-figtree text-ink-soft whitespace-nowrap">{formatDate(o.received_at)}</div>
                      <UserBadge user={o.last_edited_by || o.created_by} />
                    </div>
                    <ChevronRight className="h-4 w-4 text-ink-mute flex-shrink-0" />
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}

      {/* Drill-down modal: product breakdown for a specific brand */}
      <Dialog open={!!drilldownBrand} onOpenChange={(v) => { if (!v) setDrilldownBrand(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-outfit flex items-center gap-2" data-testid="drilldown-title">
              <span
                className="h-3.5 w-3.5 rounded-full"
                style={{ backgroundColor: canonicalColor(drilldownBrand || "") }}
              />
              {drilldownBrand} · Product Breakdown
            </DialogTitle>
          </DialogHeader>
          {drilldownData.length === 0 ? (
            <div className="text-ink-soft text-sm py-16 text-center font-figtree">
              No orders recorded for {drilldownBrand} yet.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
              <div style={{ width: "100%", height: 280 }} data-testid="drilldown-pie">
                <ResponsiveContainer>
                  <PieChart>
                    <Pie data={drilldownData} dataKey="quantity" nameKey="product" innerRadius={55} outerRadius={95} paddingAngle={2}>
                      {drilldownData.map((_, i) => <Cell key={i} fill={drilldownColors[i % drilldownColors.length]} />)}
                    </Pie>
                    <Tooltip
                      contentStyle={{ borderRadius: 8, border: "1px solid #E8E6E0", fontFamily: "Figtree" }}
                      formatter={(v, _n, p) => [`${v} units`, p?.payload?.product]}
                    />
                    <Legend wrapperStyle={{ fontSize: 12, fontFamily: "Figtree" }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div>
                <div className="text-sm font-figtree text-ink-soft mb-2">Units by product</div>
                <ul className="space-y-2">
                  {drilldownData.map((row, i) => {
                    const total = drilldownData.reduce((s, r) => s + r.quantity, 0) || 1;
                    const pct = ((row.quantity / total) * 100).toFixed(1);
                    return (
                      <li key={row.product} className="flex items-center justify-between p-2 rounded bg-bone">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: drilldownColors[i % drilldownColors.length] }} />
                          <span className="font-figtree font-medium text-ink truncate">{row.product}</span>
                        </div>
                        <div className="text-sm font-figtree flex-shrink-0 ml-3">
                          <span className="font-outfit font-semibold text-ink">{row.quantity}</span>
                          <span className="text-ink-soft ml-2">· {pct}%</span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
          <div className="pt-2">
            <button
              onClick={() => setDrilldownBrand(null)}
              data-testid="drilldown-close-btn"
              className="w-full px-4 py-2 rounded-lg border border-line font-figtree font-medium hover:bg-sand transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" /> Back to Dashboard
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
